import { format, formatDistanceToNow, parseISO } from 'date-fns';

// Moving to a global date format for consistency
export const formatDate = (
  date: Date | string,
  options?: {
    showTime?: boolean;
  }
) => {
  const userLocale = navigator.language || 'en-GB';

  const dateFormat = userLocale.startsWith('en-US')
    ? 'MM/dd/yyyy'
    : 'dd/MM/yyyy';
  const timeFormat = 'h:mmaa';

  let formatString = dateFormat;
  if (options?.showTime) {
    formatString += ` ${timeFormat}`;
  }

  return format(new Date(date), formatString);
};

const formatToDate = (datetime: string) =>
  format(parseISO(datetime), 'dd MMM yyyy');

const formatToFullDate = (datetime: string) =>
  format(parseISO(datetime), 'dd MMMM yyyy');

const formatToFullNumberDate = (datetime: string) =>
  format(parseISO(datetime), 'dd MM yyyy');

const formatToUSDate = (datetime: string) =>
  format(parseISO(datetime), 'MMM d yyyy');

const formatDateFromNow = (date: string) => {
  const formattedDate = formatDistanceToNow(new Date(date), {
    addSuffix: true
  }).replace('about ', '');

  return formattedDate;
};

const formatDateFromNowAbbreviated = (date: string) => {
  const abbreviatedLocale = {
    formatDistance: (token: string, count: number) => {
      const formatDistanceLocale: Record<string, string> = {
        lessThanXSeconds: '< 1 min ago',
        xSeconds: '< 1 min ago',
        halfAMinute: '< 1 min ago',
        lessThanXMinutes: '< {{count}} min ago',
        xMinutes: '{{count}} min ago',
        aboutXHours: '{{count}} hr ago',
        xHours: '{{count}} hr ago',
        xDays: '{{count}} d ago',
        aboutXWeeks: '{{count}} wk ago',
        xWeeks: '{{count}} wk ago',
        aboutXMonths: '{{count}} mo ago',
        xMonths: '{{count}} mo ago',
        aboutXYears: '{{count}} yr ago',
        xYears: '{{count}} yr ago',
        almostXYears: '{{count}} yr ago',
        overXYears: '{{count}} yr ago'
      };

      const result = formatDistanceLocale[token];
      if (result) {
        return result.replace('{{count}}', count.toString());
      }
      return token;
    }
  };

  return formatDistanceToNow(new Date(date), {
    addSuffix: false,
    locale: abbreviatedLocale
  });
};

const formatToDateTime = (datetime: string) => {
  const date = new Date(datetime);
  return format(date, 'HH:mm, d MMMM yyyy');
};

const formatToDateTimeLongFormat = (datetime: string) => {
  const date = new Date(datetime);
  return format(date, "d MMM yyyy 'at' h:mma");
};

const sortByDate = (
  a: string | undefined | null,
  b: string | undefined | null,
  order: 'asc' | 'desc' = 'desc'
) => {
  // If both dates are undefined, consider them equal
  if (!a && !b) return 0;
  // If 'dateA' is undefined, put it after 'dateB'
  if (!a) return 1;
  // If 'dateB' is undefined, put 'dateB' after 'dateA'
  if (!b) return -1;

  const dateA = new Date(a).getTime();
  const dateB = new Date(b).getTime();

  if (order === 'asc') {
    return dateA - dateB;
  }
  return dateB - dateA;
};

const formatMilliseconds = (milliseconds: number) => {
  let seconds = Math.floor(Math.abs(milliseconds) / 1000);
  let minutes = Math.floor(seconds / 60);
  seconds = seconds % 60;
  const hours = Math.floor(minutes / 60);
  minutes = minutes % 60;

  // Pad the numbers to two digits
  const hoursString = String(hours).padStart(2, '0');
  const minutesString = String(minutes).padStart(2, '0');
  const secondsString = String(seconds).padStart(2, '0');

  return `${hoursString}h:${minutesString}m:${secondsString}s`;
};

const convertHoursToDays = (hours: number) => {
  return hours / 24;
};

const convertDaysToHours = (days: number) => {
  return days * 24;
};

const isMoreThan7DaysPast = (givenDate: string): boolean => {
  const today = new Date();

  const datePlus7Days = new Date(givenDate);
  datePlus7Days.setDate(datePlus7Days.getDate() + 7);

  return today > datePlus7Days;
};

const formatMsToMinutesAndSeconds = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export {
  formatToDate,
  formatDateFromNow,
  formatDateFromNowAbbreviated,
  formatToDateTime,
  sortByDate,
  formatMilliseconds,
  convertHoursToDays,
  convertDaysToHours,
  formatToDateTimeLongFormat,
  isMoreThan7DaysPast,
  formatToFullDate,
  formatMsToMinutesAndSeconds,
  formatToFullNumberDate,
  formatToUSDate
};
